'use client';
import { useMemo, useState } from 'react';
import { useAccount, useWalletClient } from 'wagmi';
import { Address, parseUnits } from 'viem';
import Migrate from './Migrate';
import MigrateDisconnected from './MigrateDisconnected';
import { getChainConstant } from '@/domain/chain';
import { useChain } from '@/hooks/useChain';
import { Addresses } from '@/domain/addresses';
import useFloorV1TokenMerkleClaim from '@/application/migrate/useFloorV1TokenMerkleClaim';
import MerkleClaimAccounts from '@/domain/migrate';
import useClaimed from '@/application/migrate/useClaimed';

const MigrateContainer = () => {
  const chain = useChain();
  const { isConnected, address } = useAccount();
  const { data: wallet } = useWalletClient();
  const [error, setError] = useState<Error | null>(null);

  // DEV: use any valid snapshot address here to run the tx
  // as it does not require the recipient to be msg.sender
  // any account can claim the floor tokens for a valid recipient
  // const address = '0x1930388ceb9d2e57cb2d794002c94fa4dd583657';

  const { data: claimed, status: claimedStatus } = useClaimed(
    address as Address,
  );

  const isWithinMerkleTree = useMemo(() => {
    if (address) {
      return MerkleClaimAccounts[address.toLowerCase() as Address];
    }

    return false;
  }, [address]);

  const {
    balanceFloor,
    balanceSFloor,
    balanceAFloor,
    balanceGFloor,
    floorToReceive,
  } = useMemo(() => {
    if (address) {
      const merkleData = MerkleClaimAccounts[address.toLowerCase() as Address];
      if (merkleData) {
        return {
          balanceFloor: merkleData?.FLOOR
            ? BigInt(merkleData.FLOOR)
            : undefined,
          balanceGFloor: merkleData?.gFLOOR
            ? BigInt(merkleData.gFLOOR)
            : undefined,
          balanceSFloor: merkleData?.sFLOOR
            ? BigInt(merkleData.sFLOOR)
            : undefined,
          balanceAFloor: merkleData?.aFLOOR
            ? BigInt(merkleData.aFLOOR)
            : undefined,
          floorToReceive: BigInt(merkleData.allocation),
        };
      }
    }

    return {
      balanceFloor: undefined,
      balanceSFloor: undefined,
      balanceAFloor: undefined,
      balanceGFloor: undefined,
      floorToReceive: undefined,
    };
  }, [address]);

  const {
    migrate,
    error: migrateError,
    isPending,
    isConfirming,
    isConfirmed,
    receipt,
  } = useFloorV1TokenMerkleClaim({
    address,
    amount: floorToReceive,
  });

  if (!isConnected) {
    return <MigrateDisconnected />;
  }

  const watchAsset = () =>
    wallet?.watchAsset({
      options: {
        address: getChainConstant(Addresses, chain.id, 'FloorToken'),
        decimals: 18,
        symbol: 'FLOOR',
        image: 'https://floor.xyz/images/underscore.svg',
      },
      type: 'ERC20',
    });

  // migration ratio is 3.98 at time of snapshot
  const gfloorToFloorRatio = parseUnits('3.98', 9);

  const balanceFloorFromGFloor = balanceGFloor
    ? (balanceGFloor * gfloorToFloorRatio) / BigInt(1e18)
    : undefined;

  const canMigrate =
    isWithinMerkleTree && claimedStatus === 'success' && !claimed;

  const alreadyClaimed = claimedStatus === 'success' && claimed;

  return (
    <Migrate
      chain={chain}
      balanceStatus={claimedStatus}
      balanceFloor={balanceFloor}
      balanceSFloor={balanceSFloor}
      balanceAFloor={balanceAFloor}
      balanceGFloor={balanceGFloor}
      balanceFloorFromGFloor={balanceFloorFromGFloor}
      gfloorToFloorRatio={gfloorToFloorRatio}
      onMigrateClick={migrate}
      floorToReceive={floorToReceive}
      isPending={isPending}
      isConfirming={isConfirming}
      isConfirmed={isConfirmed}
      alreadyClaimed={alreadyClaimed}
      receipt={receipt}
      setError={setError}
      error={error || migrateError}
      canMigrate={canMigrate}
      watchAsset={watchAsset}
    />
  );
};

export default MigrateContainer;
